import {
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
} from "@chakra-ui/react";
import { addNewRecordsWithFieldSpec } from "utils/apollo/cacheHelpers";
import { useAdminMutation } from "utils/apollo/hooks/admin";
import { assertPresence } from "utils/assertions";
import { DocumentType, gql } from "__generated__/gql";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import DrawerFormFooter from "components/Shared/DrawerForm/DrawerFormFooter";
import Form, { FormFields, processApolloError } from "./Form";

const FORM_ID = "create-retailer-location-form";

const createMutation = gql(/* GraphQL */ `
  mutation CreateRetailerLocationGroup(
    $data: RetailerLocationGroupInput!
    $isAdmin: Boolean = false
  ) {
    createRetailerLocationGroup(input: { retailerLocationGroup: $data }) {
      retailerLocationGroup {
        id
        retailerId
        ...RetailerLocationGroupsTableRow
        ...SelectedRetailerLocationGroupForm
        ...SelectedRetailerLocationGroupFragment
      }
    }
  }
`);

type Props = {
  defaultValues?: Partial<FormFields>;
  isAdmin?: boolean;
  onCancel: () => void;
  onCompleted: (data: DocumentType<typeof createMutation>) => void;
  retailerId?: UUID;
};

export default function Create({
  defaultValues,
  isAdmin,
  onCancel,
  onCompleted,
  retailerId,
}: Props) {
  const [performCreate, { loading: isUpdating }] = useAdminMutation(
    createMutation,
    {
      update: (cache, result) => {
        const newLocationGroup =
          result.data?.createRetailerLocationGroup?.retailerLocationGroup;

        assertPresence(newLocationGroup);

        addNewRecordsWithFieldSpec({
          baseFieldName: "retailerLocationGroups",
          cache,
          id: newLocationGroup.retailerId,
          newObjs: [newLocationGroup],
          typename: "Retailer",
        });

        addNewRecordsWithFieldSpec({
          baseFieldName: "retailerLocationGroups",
          cache,
          newObjs: [newLocationGroup],
          getArgs: (defaultArgs) => [
            defaultArgs,
            {
              ...defaultArgs,
              filter: {
                retailerId: { equalTo: newLocationGroup.retailerId },
              },
            },
          ],
        });
      },
      onCompleted,
    },
  );

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    (formData: FormFields) =>
      performCreate({ variables: { data: formData, isAdmin } }),
    {
      processApolloError,
      successMessage: "Retailer location group created successfully",
    },
  );

  return (
    <DrawerContent bg="cardBackground">
      <DrawerCloseButton />
      <DrawerHeader>Create Retailer Location Group</DrawerHeader>
      <DrawerBody>
        <Form
          autoFocus
          defaultValues={defaultValues}
          onValidSubmit={handleValidSubmit}
          id={FORM_ID}
          isAdmin={isAdmin}
          retailerId={retailerId}
        />
      </DrawerBody>

      <DrawerFormFooter
        onClose={onCancel}
        formId={FORM_ID}
        submitButtonProps={{ isLoading: isUpdating }}
      />
    </DrawerContent>
  );
}
